import * as yup from 'yup';

import { useCallback } from 'react';

import { useUpdateVersion } from '../api/items.api';
import checkVersionId from '../helpers/checkVersionId.helper';
import { TDocument, TVersion } from '@modules/documents';
import { useUpdateOriginalVersion } from '@modules/documents/api';
import { TFile, TFileVersion } from '@modules/files';

import { TSingleFieldDialogState } from '@shared/components';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';

const useUpdateVersionDialog = () => {
  const updateVersion = useUpdateVersion();
  const updateOriginalVersion = useUpdateOriginalVersion();
  const { change } = useSearchParams();

  const openDialog = useCallback(
    (
      title: string,
      defaultText: string,
      versions: (TVersion | TFileVersion)[],
      update: (versionId: string) => Promise<void>,
      setSimpleDialogState: (state?: TSingleFieldDialogState<'input'>) => void,
      simpleDialogState?: TSingleFieldDialogState<'input'>,
      originalVersionId?: string
    ) => {
      setSimpleDialogState({
        isOpen: true,
        title,
        label: 'Version number',
        defaultText,
        buttonLabel: 'Update',
        schema: yup
          .string()
          .test('check-version-id', '', function (value) {
            const errorMessage = checkVersionId(value || '', versions, originalVersionId);
            if (errorMessage) return this.createError({ message: errorMessage });
            return true;
          })
          .required('Version number is required'),
        onSubmit: async ({ text }) => {
          try {
            await update(text);
            setSimpleDialogState(simpleDialogState ? { ...simpleDialogState, isOpen: false } : undefined);
          } catch {
            // The API hook shows the error; keep the value available for correction or retry.
          }
        },
      });
    },
    []
  );

  const open = (
    item: TDocument | TFile,
    itemType: 'DOCUMENT' | 'FILE',
    version: TVersion | TFileVersion,
    setState: (state?: TSingleFieldDialogState<'input'>) => void,
    state?: TSingleFieldDialogState<'input'>
  ) =>
    openDialog(
      'Update version number',
      version.id,
      item.versions,
      async (versionId) => {
        await updateVersion.mutateAsync({ id: item._id, itemType, versionId });
        change('view', versionId);
      },
      setState,
      state,
      itemType === 'DOCUMENT' ? (item as TDocument).originalVersionId : undefined
    );

  const openOriginal = (item: TDocument, setState: (state?: TSingleFieldDialogState<'input'>) => void) =>
    openDialog(
      'Update original version number',
      item.originalVersionId || '',
      [],
      async (versionId) => {
        await updateOriginalVersion.mutateAsync({ id: item._id, versionId });
      },
      setState
    );

  return { open, openOriginal };
};

export { useUpdateVersionDialog };
